import React, { useState } from "react";
import Button from "@/components/forms/Button/Button";
import styles from "./profile.module.css";
import { useLocation } from "wouter";
import IconSyncInventory from "@/components/icons/IconSyncInventory";
import { useUserContext } from "@/contexts/UserContext";
import TextInput from "@/components/forms/TextInput/TextInput";
import { ErrorCode, PaymentStatus, UserRole } from "@boxem/core-api-schema";
import { changeEmail, changeName, changePassword, disconnectSellerAccount } from "@/api/auth";
import IconEditGoogle from "@/components/icons/IconEditGoogle";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import SellerAccountBanner, { collapseAllSyncStatus } from "@/components/SellerAccountBanner/SellerAccountBanner";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import Flex from "@/components/Flex/Flex";
import Checklist from "@/components/Checklist/Checklist";
import PageContainer from "../container/PageContainer";
import IconLogout from "@/components/icons/IconLogout";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import PageLoader from "@/components/PageLoader";

type SettingsProfilePageProps = {};

const SettingsProfilePage = ({ }: SettingsProfilePageProps) => {
  const [location, navigate] = useLocation();
  const { resyncSellerData, resyncAllSalesData, user, setUser, seller, logout } = useUserContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { showNotification } = useNotificationContext();
  const [resyncSellerDataModalOpen, setResyncSellerDataModalOpen] = useState<boolean>(false);
  const [resyncAllSalesDataModalOpen, setResyncAllSalesDataModalOpen] = useState<boolean>(false);
  const [disconnectSellerAccountModalOpen, setDisconnectSellerAccountModalOpen] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [editNameModalOpen, setEditNameModalOpen] = useState<boolean>(false);
  const [editNameFullName, setEditNameFullName] = useState<string>("");

  const [editEmailModalOpen, setEditEmailModalOpen] = useState<boolean>(false);
  const [editEmailEmail, setEditEmailEmail] = useState<string>("");
  const [editEmailConfirmEmail, setEditEmailConfirmEmail] = useState<string>("");

  const [editPasswordModalOpen, setEditPasswordModalOpen] = useState<boolean>(false);
  const [editPasswordPasswordWrong, setEditPasswordPasswordWrong] = useState<boolean>(false);
  const [editPasswordCurrentPassword, setEditPasswordCurrentPassword] = useState<string>("");
  const [editPasswordNewPassword, setEditPasswordNewPassword] = useState<string>("");
  const [editPasswordConfirmPassword, setEditPasswordConfirmPassword] = useState<string>("");

  // you can initiate a re-sync via this page, but only if you're already in a 'completed' state.
  // otherwise, a sync is either in progress already, or you're in an error state, in which case
  // the SellerAccountBanner will prompt you to re-sync instead.
  const canResync: boolean = collapseAllSyncStatus(seller)[0] === "COMPLETE";
  const sellerConnected = !!user.sellerId;

  if (!seller) {
    return <PageLoader/>;
  }

  return (
    <PageContainer>
      <ConfirmModal
        open={resyncSellerDataModalOpen}
        loading={loading}
        onClose={(confirmed: boolean) => {
          if (confirmed) {
            setLoading(true);
            resyncSellerData().finally(() => {
              setLoading(false);
              setResyncSellerDataModalOpen(false)
            });
          } else {
            setResyncSellerDataModalOpen(false);
          }
        }}
        title="Irreversible Action - Re-Sync Data"
        body={
          <p style={{ margin: "20px" }}>
            Clicking 'Confirm' will initiate a re-sync of all your seller data to your Boxem account. This is a process that can take up to a <b>few hours</b>, during which you will be <b>unable to view your inventory or shipments, or create new shipments</b>.
          </p>
        }
        confirmButtonLabel="Confirm and initiate a re-sync"
      />

      <ConfirmModal
        loading={loading}
        open={resyncAllSalesDataModalOpen}
        onClose={(confirmed: boolean) => {
          if (confirmed) {
            setLoading(true);
            resyncAllSalesData().finally(() => {
              setLoading(false);
              setResyncAllSalesDataModalOpen(false)
            });
          } else {
            setResyncAllSalesDataModalOpen(false);
          }
        }}
        title="Irreversible Action - Re-Sync Sales Data"
        body={
          <p style={{ margin: "20px" }}>
            Clicking 'Confirm' will initiate a re-sync of all your sales and fee data to your Boxem account. This is a process that can take up to a <b>24 hours</b>, during which the sales and fee totals for your dashboard may be inaccurate<b></b>.
          </p>
        }
        confirmButtonLabel="Confirm and initiate a sales re-sync"
      />

      <ConfirmModal
        open={disconnectSellerAccountModalOpen}
        onClose={(confirmed: boolean) => {
          if (confirmed) {
            disconnectSellerAccount().then(res => setUser(res as any /* @TODO fix 'any' here... */, false)).finally(() => setDisconnectSellerAccountModalOpen(false));
          } else {
            setDisconnectSellerAccountModalOpen(false);
          }
        }}
        title="Disconnect seller account"
        body={
          <p style={{ margin: "20px" }}>
            Disconnecting your Seller ID will remove access to all data in your account. After 180 days, if not reconnected, your Boxem data will be permanently deleted. Are you sure you wish to continue?.
          </p>
        }
        confirmButtonLabel="Disconnect seller account"
      />

      <ConfirmModal
        width={"30rem"}
        loading={loading}
        open={editNameModalOpen}
        onClose={confirmed => {
          if (confirmed) {
            setLoading(true);
            changeName(editNameFullName)
              .then(() => {
                showNotification("Changed name successfully!", NotificationType.SUCCESS);
                setUser({ ...user, name: editNameFullName }, false)
              })
              .catch(e => {
                if (handleErrorResponseGenerically(e)) {
                  return;
                }
                if (e?.[0]?.type === ErrorCode.REQUIRES_EMAIL_VERIFICATION) {
                  showNotification("You must verify your email before changing your name!", NotificationType.ERROR);
                }
              })
              .finally(() => {
                setLoading(false);
                setEditNameModalOpen(false);
              })
          } else {
            setEditNameModalOpen(false);
          }
          setEditNameFullName("");
          setEditNameModalOpen(false);
        }}
        title={"Change Full Name"}
        body={
          <Flex direction="column" style={{ margin: "40px 20px" }} gap={4}>
            <Flex direction="column" gap={2}>
              <label htmlFor="currentFullName">
                Current full name
              </label>
              <TextInput
                inputId="currentFullName"
                isReadonly
                value={user.name}
              />
            </Flex>
            <Flex direction="column" gap={2}>
              <label htmlFor="newFullName">
                New full name
              </label>
              <TextInput
                inputId="newFullName"
                placeholder="Enter a new full name"
                minLength={1}
                maxLength={255}
                autocomplete="name"
                name="name"
                value={editNameFullName}
                onChange={v => setEditNameFullName(v)}
              />
            </Flex>
          </Flex>
        }
        confirmButtonLabel="Save"
      />

      <ConfirmModal
        width={"min(50rem, 50vw)"}
        loading={loading}
        open={editEmailModalOpen}
        onClose={confirmed => {
          if (confirmed) {
            if (editEmailEmail !== "" && !/^[^@]+@[^@]+$/.test(editEmailEmail)) {
              return;
            }
            if (editEmailEmail !== "" && editEmailConfirmEmail !== "" && editEmailEmail !== editEmailConfirmEmail) {
              return;
            }
            setLoading(true);
            const lowered = editEmailEmail.toLowerCase();
            changeEmail({ email: lowered })
              .then(() => {
                showNotification("Changed email successfully. Please check your email inbox for a confirmation link to re-activate your account.", NotificationType.SUCCESS);
                setUser({ ...user, email: lowered, emailVerified: false }, false);
                navigate("~/auth/verify-email");
              })
              .catch(e => {
                if (handleErrorResponseGenerically(e)) {
                  return;
                }
                if (e?.[0]?.type === ErrorCode.REQUIRES_EMAIL_VERIFICATION) {
                  showNotification("You must verify your email before changing it!", NotificationType.ERROR);

                } else if (e?.[0]?.type === ErrorCode.CHANGE_EMAIL_EMAIL_ALREADY_EXISTS) {
                  showNotification("Invalid email - may already be in use.", NotificationType.ERROR);
                }
              })
              .finally(() => {
                setLoading(false);
                setEditEmailModalOpen(false);
              })
          } else {
            setEditEmailModalOpen(false);
          }

          setEditEmailEmail("");
        }}
        title={"Change Email"}
        body={
          <Flex direction="column" style={{ margin: "40px 20px" }} gap={4}>
            <Flex direction="column" gap={2}>
              <label htmlFor="currentEmail">
                Current email address
              </label>
              <TextInput
                inputId="currentEmail"
                isReadonly
                value={user.email}
              />
            </Flex>
            <Flex direction="column" gap={2}>
              <label htmlFor="newEmail">
                New email address
              </label>
              <TextInput
                inputId="newEmail"
                placeholder="Enter a new email address"
                minLength={1}
                maxLength={255}
                autocomplete="email"
                name="email"
                value={editEmailEmail}
                onChange={v => setEditEmailEmail(v)}
              />
            </Flex>
            <Flex direction="column" gap={2}>
              <label htmlFor="newEmail">
                Confirm new email address
              </label>
              <TextInput
                inputId="newEmail"
                placeholder="Confirm your email address"
                minLength={1}
                maxLength={255}
                autocomplete="email"
                name="email"
                value={editEmailConfirmEmail}
                onChange={v => setEditEmailConfirmEmail(v)}
              />
            </Flex>

            {(editEmailEmail !== "" && !/^[^@]+@[^@]+$/.test(editEmailEmail)) && (
              <p className={`small-text ${styles.invalidText}`}>
                Email is invalid
              </p>
            )}
            {(editEmailEmail !== "" && editEmailConfirmEmail !== "" && editEmailEmail !== editEmailConfirmEmail) && (
              <p className={`small-text ${styles.invalidText}`}>
                Emails don't match
              </p>
            )}
          </Flex>
        }
        confirmButtonLabel="Save"
      />

      <ConfirmModal
        width={"30rem"}
        loading={loading}
        open={editPasswordModalOpen}
        onClose={confirmed => {
          if (confirmed) {
            setLoading(true);
            changePassword({ currentPassword: editPasswordCurrentPassword, newPassword: editPasswordNewPassword })
              .then(() => {
                showNotification("Changed password successfully!", NotificationType.SUCCESS);
              })
              .catch(e => {
                if (handleErrorResponseGenerically(e)) {
                  return;
                }

                if (e?.[0]?.type === ErrorCode.REQUIRES_EMAIL_VERIFICATION) {
                  showNotification("You must verify your email before changing your password!", NotificationType.ERROR);
                }
              })
              .finally(() => {
                setLoading(false);
                setEditPasswordModalOpen(false);
              })
          } else {
            setEditPasswordModalOpen(false);
          }

          setEditPasswordCurrentPassword("");
          setEditPasswordNewPassword("");
          setEditPasswordConfirmPassword("");
        }}
        title="Change Password"
        body={
          <Flex direction="column" style={{ margin: "40px 20px" }} gap={4}>
            <Flex direction="column" gap={2}>
              <label htmlFor="currentPassword">
                Current password
              </label>
              <TextInput
                inputId="currentPassword"
                type="password"
                placeholder="Enter your current password"
                maxLength={255}
                invalid={editPasswordPasswordWrong}
                autocomplete="current-password"
                name="current-password"
                value={editPasswordCurrentPassword}
                onChange={v => setEditPasswordCurrentPassword(v)}
              />
            </Flex>
            {editPasswordPasswordWrong && (
              <p className={`small-text ${styles.invalidText}`}>
                Password is incorrect!
              </p>
            )}

            <Flex direction="column" gap={2}>
              <label htmlFor="newPassword">
                New password
              </label>
              <TextInput
                inputId="newPassword"
                type="password"
                placeholder="Enter your new password"
                maxLength={255}
                autocomplete="new-password"
                name="new-password"
                value={editPasswordNewPassword}
                onChange={v => setEditPasswordNewPassword(v)}
              />
            </Flex>

            <Flex direction="column" gap={2}>
              <label htmlFor="newPasswordConfirm">
                Confirm new password
              </label>
              <TextInput
                inputId="newPasswordConfirm"
                type="password"
                placeholder="Confirm your new password"
                maxLength={255}
                invalid={editPasswordConfirmPassword !== editPasswordNewPassword}
                value={editPasswordConfirmPassword}
                onChange={v => setEditPasswordConfirmPassword(v)}
              />
            </Flex>
            {editPasswordNewPassword !== editPasswordConfirmPassword && (
              <p className={`small-text ${styles.invalidText}`}>
                Passwords do not match
              </p>
            )}

            {(() => {
              const password = editPasswordNewPassword;
              const hasLowercase = /[a-z]/.test(password);
              const hasUppercase = /[A-Z]/.test(password);
              const hasSpecial = /[$@$!%*?&_~:`+|.<>#{}()\\-]/.test(password);
              const hasNumber = /[0-9]/.test(password);

              return <Checklist
                color="success"
                items={[
                  { label: "Should contain at least 1 lowercase character", checked: password && hasLowercase },
                  { label: "Should contain at least 1 uppercase character", checked: password && hasUppercase },
                  { label: "Should contain at least 1 special character", checked: password && hasSpecial },
                  { label: "Should contain at least 1 number", checked: password && hasNumber },
                  { label: "Should be at least 8 characters", checked: password?.length >= 8 },
                ]}
              />
            })()}
          </Flex>
        }
        confirmButtonLabel="Save"
      />

      <div className={styles.profileSettingsContainer}>
        <SellerAccountBanner />

        <div className={styles.profileHeader}>Profile</div>

        {user?.role === UserRole.SELLER && (
          <>
            <div className={styles.settingContainer}>
              <div className={styles.settingLabelContainer}>
                <div className={styles.settingLabelHeaderText}>
                  Amazon Seller Account
                </div>
                <div className={styles.settingLabelSubText}>
                  Link your Amazon Seller Account to get the most out of Boxem.
                </div>
              </div>
              <div className={styles.settingControlsContainer}>
                <div className={styles.settingControlsLabel}>
                  Amazon Seller Account
                </div>
                <div className={styles.linkedContainer}>
                  <div className={styles.sellerInfoContainer}>
                    <div className={styles.amazonIconContainer}>
                      <img
                        src="/images/amazon-logo-HD.jpeg"
                        className={styles.amazonIcon}
                      />
                      {sellerConnected && <div className={styles.activeDot} />}
                    </div>
                    <div className={styles.sellerInfoTextContainer}>
                      <div className={styles.sellerInfoText}>
                        {sellerConnected ? (
                          <>
                            Seller ID: {user.sellerId}
                          </>
                        ) : (
                          <>
                            Not linked
                          </>
                        )}
                      </div>
                      <div className={styles.marketplaceInfoText}>
                        {sellerConnected ? (
                          <>
                            Marketplace ID: {user.activeMarketplaceId}
                          </>
                        ) : (
                          <>
                            Link your Amazon Seller Account.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {sellerConnected ? (
                    <Button type="text" onClick={() => setDisconnectSellerAccountModalOpen(true)}>
                      <span className="strong">Disconnect</span>
                    </Button>
                  ) : (
                    <Button disabled={!seller.isSubscribed || !seller.emailVerified || seller.paymentStatus === PaymentStatus.PAST_DUE} type="primary" onClick={() => navigate("~/connect/seller-api")}>
                      Link Amazon Seller Account
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.divider} />
          </>
        )}

        <div className={styles.settingContainer}>
          <div className={styles.settingLabelContainer}>
            <div className={styles.settingLabelHeaderText}>
              Account
            </div>
            <div className={styles.settingLabelSubText}>
              Manage your account details and password.
            </div>
            <div className={styles.logoutContainer}>
              <Button type="primary" onClick={() => logout()}>
                <IconLogout />
                Logout
              </Button>
            </div>
          </div>
          <div className={styles.settingControlsContainer}>
            <div className={styles.settingControlsLabel}>
              Full name
            </div>
            <TextInput
              className={styles.readonlyInput}
              isReadonly
              value={user.name}
              onClick={() => setEditNameModalOpen(true)}
              suffix={
                <IconEditGoogle
                  className={styles.pencilIcon}
                  size={16}
                />
              }
            />
            <div className={styles.space} />

            <div className={styles.settingControlsLabel}>
              Email address
            </div>
            <TextInput
              className={styles.readonlyInput}
              isReadonly
              value={user.email}
              onClick={() => setEditEmailModalOpen(true)}
              suffix={
                <IconEditGoogle
                  className={styles.pencilIcon}
                  size={16}
                />
              }
            />
            <div className={styles.space} />

            <div className={styles.settingControlsLabel}>
              Password
            </div>
            <TextInput
              className={styles.readonlyInput}
              isReadonly
              value="•••••••••••••••"
              onClick={() => setEditPasswordModalOpen(true)}
              suffix={
                <IconEditGoogle
                  className={styles.pencilIcon}
                  size={16}
                />
              }
            />
          </div>
        </div>

        {(sellerConnected && canResync && user?.role === UserRole.SELLER) && (
          <>
            <div className={styles.divider} />
            <div className={styles.settingContainer}>
              <div className={styles.settingLabelContainer}>
                <div className={styles.settingLabelHeaderText}>
                  Danger Zone
                </div>
                <div className={styles.settingLabelSubText}>
                  Proceed with caution! Actions in the danger zone will lead to complete deletion of certain data.
                </div>
              </div>
              <div className={styles.settingButtonContainer}>
                <Button
                  type="danger"
                  onClick={() => setResyncSellerDataModalOpen(true)}
                >
                  <IconSyncInventory size={20} />
                  <span style={{ marginLeft: "1ch" }}>Re-Sync with Amazon</span>
                </Button>
                <Button
                  type="danger"
                  onClick={() => setResyncAllSalesDataModalOpen(true)}
                >
                  <IconSyncInventory size={20} />
                  <span style={{ marginLeft: "1ch" }}>Re-Sync Sales Data</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default SettingsProfilePage;
